import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, CheckCircle, FileText, Image } from 'lucide-react';
import { 
  FileUploadZoneProps, 
  FileUploadProgress, 
  ALLOWED_FILE_TYPES, 
  MAX_FILE_SIZE, 
  MAX_FILES_PER_UPLOAD,
  validateFile,
  formatFileSize,
  getFileIcon
} from '../../types/file-attachments';
import { validateFiles } from '../../utils/file-attachments/validation';

interface FileUploadZoneState {
  isDragOver: boolean;
  selectedFiles: File[];
  validationResults: Map<string, { isValid: boolean; errors: string[]; warnings: string[] }>;
  isValidating: boolean;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFilesSelected,
  acceptedTypes = ALLOWED_FILE_TYPES.all,
  maxFileSize = MAX_FILE_SIZE,
  maxFiles = MAX_FILES_PER_UPLOAD,
  disabled = false,
  className = '',
  children
}) => {
  const [state, setState] = useState<FileUploadZoneState>({
    isDragOver: false,
    selectedFiles: [],
    validationResults: new Map(),
    isValidating: false
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setState(prev => ({ ...prev, isDragOver: true }));
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState(prev => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;
    
    setState(prev => ({ ...prev, isDragOver: false, isValidating: true }));
    
    const files = Array.from(e.dataTransfer.files);
    await processFiles(files);
  }, [disabled]);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files) return;
    
    setState(prev => ({ ...prev, isValidating: true }));
    
    const files = Array.from(e.target.files);
    await processFiles(files);
  }, [disabled]);

  const processFiles = useCallback(async (files: File[]) => {
    try {
      // Initial file count validation
      if (files.length === 0) {
        setState(prev => ({ ...prev, isValidating: false }));
        return;
      }

      if (files.length > maxFiles) {
        setState(prev => ({ 
          ...prev, 
          isValidating: false,
          validationResults: new Map([
            ['general', { 
              isValid: false, 
              errors: [`Too many files selected. Maximum ${maxFiles} files allowed.`], 
              warnings: [] 
            }]
          ])
        }));
        return;
      }

      // Validate each file
      const validationResults = await validateFiles(files, 'general');
      const resultMap = new Map();
      
      validationResults.forEach(({ file, result }, index) => {
        resultMap.set(`${file.name}-${index}`, result);
      });

      setState(prev => ({
        ...prev,
        selectedFiles: files,
        validationResults: resultMap,
        isValidating: false
      }));

      // If all files are valid, call onFilesSelected
      const validFiles = validationResults
        .filter(({ result }) => result.isValid)
        .map(({ file }) => file);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }

    } catch (error) {
      console.error('File processing error:', error);
      setState(prev => ({ 
        ...prev, 
        isValidating: false,
        validationResults: new Map([
          ['general', { 
            isValid: false, 
            errors: ['An error occurred while processing files'], 
            warnings: [] 
          }]
        ])
      }));
    }
  }, [maxFiles, onFilesSelected]);

  const removeFile = useCallback((index: number) => {
    setState(prev => {
      const newFiles = prev.selectedFiles.filter((_, i) => i !== index);
      const newResults = new Map();
      
      // Update validation results map
      prev.validationResults.forEach((result, key) => {
        const fileIndex = parseInt(key.split('-').pop() || '0');
        if (fileIndex < index) {
          newResults.set(key, result);
        } else if (fileIndex > index) {
          const newKey = key.replace(/-\d+$/, `-${fileIndex - 1}`);
          newResults.set(newKey, result);
        }
      });

      return {
        ...prev,
        selectedFiles: newFiles,
        validationResults: newResults
      };
    });
  }, []);

  const clearFiles = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedFiles: [],
      validationResults: new Map()
    }));
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const openFileDialog = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const getFileTypeIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    }
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  const hasErrors = Array.from(state.validationResults.values()).some(result => !result.isValid);
  const hasWarnings = Array.from(state.validationResults.values()).some(result => result.warnings.length > 0);

  return (
    <div className={`file-upload-zone ${className}`}>
      {/* Main Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200
          ${state.isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-400'}
          ${hasErrors ? 'border-red-300 bg-red-50' : ''}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {children ? (
          children
        ) : (
          <div className="space-y-3">
            <Upload className={`w-12 h-12 mx-auto ${disabled ? 'text-gray-400' : 'text-gray-500'}`} />
            
            {state.isValidating ? (
              <div className="text-blue-600">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                Validating files...
              </div>
            ) : (
              <>
                <h3 className={`text-lg font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>
                  Drop files here or click to browse
                </h3>
                <p className={`text-sm ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
                  Maximum {maxFiles} files, {formatFileSize(maxFileSize)} each
                </p>
                <p className="text-xs text-gray-400">
                  Supported: PDF, Word, Excel, Images, Text files
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* File List */}
      {state.selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">
              Selected Files ({state.selectedFiles.length})
            </h4>
            <button
              onClick={clearFiles}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear all
            </button>
          </div>

          {state.selectedFiles.map((file, index) => {
            const resultKey = `${file.name}-${index}`;
            const result = state.validationResults.get(resultKey);
            const isValid = result?.isValid ?? true;
            const errors = result?.errors ?? [];
            const warnings = result?.warnings ?? [];

            return (
              <div
                key={`${file.name}-${index}`}
                className={`
                  flex items-center justify-between p-3 border rounded-lg
                  ${isValid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}
                `}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileTypeIcon(file)}
                  
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isValid ? 'text-gray-900' : 'text-red-900'}`}>
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    {isValid ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error Messages */}
      {hasErrors && (
        <div className="mt-4 p-3 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="text-sm font-medium text-red-900 mb-1">
                Some files have validation errors:
              </h5>
              <ul className="text-sm text-red-700 space-y-1">
                {Array.from(state.validationResults.entries()).map(([key, result]) => 
                  result.errors.map((error, index) => (
                    <li key={`${key}-error-${index}`}>• {error}</li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Warning Messages */}
      {hasWarnings && !hasErrors && (
        <div className="mt-4 p-3 border border-yellow-200 rounded-lg bg-yellow-50">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="text-sm font-medium text-yellow-900 mb-1">
                Please note:
              </h5>
              <ul className="text-sm text-yellow-700 space-y-1">
                {Array.from(state.validationResults.entries()).map(([key, result]) => 
                  result.warnings.map((warning, index) => (
                    <li key={`${key}-warning-${index}`}>• {warning}</li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;